<template>
    <SmuMainMenu :sections="filteredSections" :current-route="route.path">
    </SmuMainMenu>
    <div
        v-if="isAuthorised({ permission: 'manage go-to-legacy-backend' })"
        class="mt-4"
    >
        <MenuRedirect />
    </div>
</template>

<script setup lang="ts">
import { type SmuIconProps, SmuMainMenu } from "@smakecloud/smake-ui";

type Link = {
    name: string;
    beta?: boolean;
    dev?: boolean;
    icon: SmuIconProps;
    permission?: string;
    url: string;
};

type Section = {
    name: string;
    links: Link[];
};

const { t } = useI18n();
const { isAuthorised } = useAuthorisationChecker();
const router = useRouter();
const route = useRoute();
const { slug } = storeToRefs(useSlugStore());

const sections = computed((): Section[] =>
    slug.value === undefined
        ? []
        : [
              {
                  name: "",
                  links: [
                      {
                          name: t("shop/backend.dashboard.menu.title"),
                          icon: {
                              icon: "tachometer-alt",
                              type: "light",
                          },
                          url: `/${slug.value}/dashboard`,
                      },
                  ],
              },
              {
                  name: t("shop/backend.sidebar.catalogs"),
                  links: [
                      {
                          name: t("shop/backend.products.menu.title"),
                          icon: {
                              icon: "barcode",
                              type: "light",
                          },
                          permission: "manage product",
                          url: `/${slug.value}/products`,
                      },
                      {
                          name: t("shop/backend.modifications.menu.title"),
                          icon: {
                              icon: "hexagon-image",
                              type: "light",
                          },
                          permission: "manage logo",
                          url: `/${slug.value}/logos`,
                      },
                      {
                          name: t("eloquent.models.customer", 2),
                          icon: {
                              icon: "users",
                              type: "light",
                          },
                          permission: "manage customer",
                          url: `/${slug.value}/customers`,
                      },
                  ],
              },
              {
                  name: t("shop/backend/app.settings.menu.title"),
                  links: [
                      {
                          name: t("eloquent.models.product_size_scale", 2),
                          icon: {
                              icon: "ruler-combined",
                              type: "light",
                          },
                          url: `/${slug.value}/product-size-scales`,
                      },
                  ],
              },
              {
                  name: t("system.backend.tools.title"),
                  links: [
                      {
                          name: t("image_check"),
                          icon: {
                              icon: "ruler",
                              type: "light",
                          },
                          url: `/${slug.value}/raster-analyzer`,
                      },
                      {
                          name: "Vektorisierer",
                          icon: {
                              icon: "bezier-curve",
                              type: "light",
                          },
                          url: `/${slug.value}/vectorize`,
                      },
                  ],
              },
          ],
);

const filteredSections = computed(() =>
    sections.value
        .filter((section) =>
            section.links.some((link) => {
                const resolvedRoute = router.resolve(link.url);

                return isAuthorised({
                    dev: resolvedRoute.meta.dev,
                    feature: resolvedRoute.meta.feature,
                    permission: resolvedRoute.meta.permission,
                });
            }),
        )
        .map((section) => ({
            ...section,
            links: section.links.filter((link) => {
                const resolvedRoute = router.resolve(link.url);

                return isAuthorised({
                    dev: resolvedRoute.meta.dev,
                    feature: resolvedRoute.meta.feature,
                    permission: resolvedRoute.meta.permission,
                });
            }),
        })),
);
</script>
